import {
    useWindowDimensions,
    StyleSheet,
    ScrollView,
    View
} from "react-native";
import {
    BrandMark
} from "./brand";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    Breadcrumb,
    Separator,
    Button,
    Table,
    Badge,
    Card,
    Text
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../i18n";
import {
    Download,
    Printer,
    Check,
    Mail
} from "lucide-react-native";
import {
    createKitIcon
} from "../components/kitIcons";

const PrinterIcon = createKitIcon(Printer);

const DownloadIcon = createKitIcon(Download);

const MailIcon = createKitIcon(Mail);

const CheckIcon = createKitIcon(Check);

const VAT_RATE = 0.2;

const INVOICE_DATE = new Date(2026, 10, 1);

const DUE_DATE = new Date(2026, 10, 15);

const PAID_DATE = new Date(2026, 10, 1, 14, 32);

const ITEMS = [
    {
        id: "yirgacheffe",
        unitPrice: 820,
        quantity: 12
    },
    {
        unitPrice: 740,
        quantity: 12,
        id: "huila"
    },
    {
        unitPrice: 610,
        id: "cerrado",
        quantity: 20
    },
    {
        unitPrice: 145,
        quantity: 10,
        id: "filter"
    }
];

const META_KEYS = [
    "number",
    "date",
    "due",
    "order"
];

const BANK_KEYS = [
    "bank",
    "holder",
    "iban",
    "reference"
];

const stylesheet = StyleSheet.create({
    toolbar: {
        justifyContent: "space-between",
        alignItems: "center",
        flexDirection: "row",
        flexWrap: "wrap"
    },
    pair: {
        justifyContent: "space-between",
        flexDirection: "row"
    },
    stamp: {
        alignSelf: "flex-start",
        transform: [
            {
                rotate: "-3deg"
            }
        ]
    },
    title: {
        alignItems: "flex-end"
    },
    split: {
        flexDirection: "row",
        flexWrap: "wrap"
    },
    page: {
        alignItems: "center",
        minHeight: "100%",
        flexGrow: 1
    },
    crumbs: {
        alignItems: "center",
        flexDirection: "row",
        flexWrap: "wrap"
    },
    actions: {
        flexDirection: "row",
        flexWrap: "wrap"
    },
    centerRow: {
        alignItems: "center"
    },
    block: {
        flexShrink: 1,
        minWidth: 0,
        flexGrow: 1
    },
    sheet: {
        width: "100%"
    }
});

const handlePress = () => undefined;

const Invoice01 = () => {
    const {
        t
    } = useDocsI18n();

    const {
        activeLocale
    } = NCoreUIKitLocalize.useContext();

    const {
        shapeConfigs,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    const {
        width
    } = useWindowDimensions();

    const isCompact = width < shapeConfigs.compactBreakpoint;

    const pagePadding = isCompact ? spaces.spacingMd : spaces.spacingLg;

    const currency = new Intl.NumberFormat(activeLocale, {
        style: "currency",
        currency: "TRY"
    });

    const percent = new Intl.NumberFormat(activeLocale, {
        style: "percent"
    });

    const dateFormat = new Intl.DateTimeFormat(activeLocale, {
        day: "numeric",
        month: "short",
        year: "numeric"
    });

    const dateTimeFormat = new Intl.DateTimeFormat(activeLocale, {
        minute: "2-digit",
        hour: "2-digit",
        day: "numeric",
        month: "short",
        year: "numeric"
    });

    const subtotal = ITEMS.reduce((sum, item) => sum + item.quantity * item.unitPrice, 0);

    const vat = subtotal * VAT_RATE;

    const metaValues: Record<string, string> = {
        number: t("blocks.invoice01.invoiceNumber"),
        order: t("blocks.invoice01.orderNumber"),
        date: dateFormat.format(INVOICE_DATE),
        due: dateFormat.format(DUE_DATE)
    };

    const bankValues: Record<string, string> = {
        holder: t("blocks.invoice01.sellerName"),
        reference: metaValues.number as string,
        bank: t("blocks.invoice01.bankName"),
        iban: t("blocks.invoice01.ibanValue")
    };

    const renderMeta = (key: string) => <View
        style={[
            stylesheet.pair,
            {
                gap: spaces.spacingMd
            }
        ]}
        key={key}
    >
        <Text
            variant="bodyMediumSize"
            color="mid"
        >
            {t(`blocks.invoice01.meta_${key}`)}
        </Text>
        <Text
            variant={key === "number" || key === "order" ? "monoMediumSize" : "bodyMediumSize"}
            color="high"
        >
            {metaValues[key]}
        </Text>
    </View>;

    const renderBank = (key: string) => <View
        style={[
            stylesheet.split,
            {
                gap: spaces.spacingMd
            }
        ]}
        key={key}
    >
        <Text
            style={{
                minWidth: shapeConfigs.fieldMinWidth / 2
            }}
            variant="bodyMediumSize"
            color="mid"
        >
            {t(`blocks.invoice01.bank_${key}`)}
        </Text>
        <Text
            variant={key === "iban" || key === "reference" ? "monoMediumSize" : "bodyMediumSize"}
            color="high"
        >
            {bankValues[key]}
        </Text>
    </View>;

    const renderItem = (item: typeof ITEMS[number]) => <Table.Row
        key={item.id}
    >
        <Table.Cell>
            <Text
                variant="bodyMediumSize"
                color="high"
            >
                {t(`blocks.invoice01.item_${item.id}`)}
            </Text>
            <Text
                variant="bodySmallSize"
                color="mid"
            >
                {t(`blocks.invoice01.item_${item.id}_note`)}
            </Text>
        </Table.Cell>
        <Table.Cell
            isNumeric={true}
            isShrink={true}
        >
            {item.quantity}
        </Table.Cell>
        <Table.Cell
            isNumeric={true}
            isShrink={true}
        >
            {currency.format(item.unitPrice)}
        </Table.Cell>
        <Table.Cell
            isNumeric={true}
            isShrink={true}
        >
            {percent.format(VAT_RATE)}
        </Table.Cell>
        <Table.Cell
            isNumeric={true}
            isShrink={true}
        >
            {currency.format(item.quantity * item.unitPrice)}
        </Table.Cell>
    </Table.Row>;

    return <ScrollView
        contentContainerStyle={[
            stylesheet.page,
            {
                backgroundColor: colors.content.container.mid,
                padding: pagePadding,
                gap: spaces.spacingSm
            }
        ]}
    >
        <View
            style={[
                stylesheet.toolbar,
                stylesheet.sheet,
                {
                    maxWidth: shapeConfigs.contentMaxWidth,
                    gap: spaces.spacingMd
                }
            ]}
        >
            <View
                style={[
                    stylesheet.crumbs,
                    {
                        gap: spaces.spacingSm
                    }
                ]}
            >
                <Breadcrumb>
                    <Breadcrumb.List>
                        <Breadcrumb.Item>
                            <Breadcrumb.Link
                                title={t("blocks.invoice01.invoices")}
                                onPress={handlePress}
                            />
                        </Breadcrumb.Item>
                        <Breadcrumb.Item>
                            <Breadcrumb.Separator/>
                            <Breadcrumb.Page
                                title={metaValues.number as string}
                            />
                        </Breadcrumb.Item>
                    </Breadcrumb.List>
                </Breadcrumb>
                <Badge
                    title={t("blocks.invoice01.paid")}
                    spreadBehaviour="free"
                    variant="tonal"
                    type="success"
                    size="small"
                />
            </View>
            <View
                style={[
                    stylesheet.actions,
                    {
                        gap: spaces.spacingSm
                    }
                ]}
            >
                <Button
                    title={t("blocks.invoice01.print")}
                    onPress={handlePress}
                    icon={PrinterIcon}
                    variant="outline"
                    type="neutral"
                    size="small"
                />
                <Button
                    title={t("blocks.invoice01.download")}
                    onPress={handlePress}
                    icon={DownloadIcon}
                    variant="outline"
                    type="neutral"
                    size="small"
                />
                <Button
                    title={t("blocks.invoice01.send")}
                    onPress={handlePress}
                    variant="filled"
                    icon={MailIcon}
                    type="primary"
                    size="small"
                />
            </View>
        </View>
        <Card
            style={[
                stylesheet.sheet,
                {
                    maxWidth: shapeConfigs.contentMaxWidth
                }
            ]}
            variant="elevated"
        >
            <Card.Content
                style={{
                    gap: spaces.spacingLg
                }}
            >
                <View
                    style={[
                        stylesheet.split,
                        {
                            justifyContent: "space-between",
                            gap: spaces.spacingLg
                        }
                    ]}
                >
                    <View
                        style={[
                            stylesheet.block,
                            {
                                gap: spaces.spacingMd
                            }
                        ]}
                    >
                        <BrandMark/>
                        <View>
                            <Text
                                variant="bodySmallSize"
                                color="mid"
                            >
                                {t("blocks.invoice01.sellerName")}
                            </Text>
                            <Text
                                variant="bodySmallSize"
                                color="mid"
                            >
                                {t("blocks.invoice01.sellerAddress")}
                            </Text>
                            <Text
                                variant="bodySmallSize"
                                color="mid"
                            >
                                {t("blocks.invoice01.sellerTax")}
                            </Text>
                        </View>
                    </View>
                    <View
                        style={[
                            stylesheet.title,
                            {
                                gap: spaces.spacingXs
                            }
                        ]}
                    >
                        <Text
                            accessibilityRole="header"
                            variant="displaySmallSize"
                            color="high"
                        >
                            {t("blocks.invoice01.title")}
                        </Text>
                        <Text
                            variant="bodySmallSize"
                            color="mid"
                        >
                            {t("blocks.invoice01.kind")}
                        </Text>
                    </View>
                </View>
                <Separator
                    length="100%"
                />
                <View
                    style={[
                        stylesheet.split,
                        {
                            gap: spaces.spacingLg
                        }
                    ]}
                >
                    <View
                        style={[
                            stylesheet.block,
                            {
                                minWidth: shapeConfigs.fieldMinWidth,
                                gap: spaces.spacingXs
                            }
                        ]}
                    >
                        <Text
                            variant="labelMediumSize"
                            color="mid"
                        >
                            {t("blocks.invoice01.billedTo")}
                        </Text>
                        <Text
                            variant="titleMediumSize"
                            color="high"
                        >
                            {t("blocks.invoice01.buyerName")}
                        </Text>
                        <Text
                            variant="bodyMediumSize"
                            color="mid"
                        >
                            {t("blocks.invoice01.buyerContact")}
                        </Text>
                        <Text
                            variant="bodyMediumSize"
                            color="mid"
                        >
                            {t("blocks.invoice01.buyerAddress")}
                        </Text>
                        <Text
                            variant="bodyMediumSize"
                            color="mid"
                        >
                            {t("blocks.invoice01.buyerTax")}
                        </Text>
                    </View>
                    <View
                        style={[
                            stylesheet.block,
                            {
                                minWidth: shapeConfigs.fieldMinWidth,
                                gap: spaces.spacingSm
                            }
                        ]}
                    >
                        {META_KEYS.map(renderMeta)}
                    </View>
                </View>
                <Table
                    caption={t("blocks.invoice01.itemsCaption")}
                    isCaptionHidden={true}
                >
                    <Table.Header>
                        <Table.Row>
                            <Table.Head>
                                {t("blocks.invoice01.col_description")}
                            </Table.Head>
                            <Table.Head
                                isNumeric={true}
                                isShrink={true}
                            >
                                {t("blocks.invoice01.col_quantity")}
                            </Table.Head>
                            <Table.Head
                                isNumeric={true}
                                isShrink={true}
                            >
                                {t("blocks.invoice01.col_unitPrice")}
                            </Table.Head>
                            <Table.Head
                                isNumeric={true}
                                isShrink={true}
                            >
                                {t("blocks.invoice01.col_vat")}
                            </Table.Head>
                            <Table.Head
                                isNumeric={true}
                                isShrink={true}
                            >
                                {t("blocks.invoice01.col_amount")}
                            </Table.Head>
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        {ITEMS.map(renderItem)}
                    </Table.Body>
                </Table>
                <View
                    style={[
                        stylesheet.split,
                        {
                            gap: spaces.spacingLg
                        }
                    ]}
                >
                    <View
                        style={[
                            stylesheet.block,
                            {
                                minWidth: shapeConfigs.fieldMinWidth,
                                gap: spaces.spacingSm
                            }
                        ]}
                    >
                        <Text
                            variant="labelMediumSize"
                            color="mid"
                        >
                            {t("blocks.invoice01.paymentInfo")}
                        </Text>
                        {BANK_KEYS.map(renderBank)}
                        <View
                            style={[
                                stylesheet.split,
                                {
                                    paddingTop: spaces.spacingSm,
                                    gap: spaces.spacingMd
                                }
                            ]}
                        >
                            <View
                                style={stylesheet.stamp}
                            >
                                <Badge
                                    title={t("blocks.invoice01.stamp")}
                                    spreadBehaviour="free"
                                    variant="outline"
                                    icon={CheckIcon}
                                    type="success"
                                    size="large"
                                />
                            </View>
                            <View>
                                <Text
                                    variant="bodySmallSize"
                                    color="mid"
                                >
                                    {dateTimeFormat.format(PAID_DATE)}
                                </Text>
                                <Text
                                    variant="bodySmallSize"
                                    color="mid"
                                >
                                    {t("blocks.invoice01.paidBy")}
                                </Text>
                            </View>
                        </View>
                    </View>
                    <View
                        style={[
                            stylesheet.block,
                            {
                                minWidth: shapeConfigs.fieldMinWidth,
                                gap: spaces.spacingSm
                            }
                        ]}
                    >
                        <View
                            style={stylesheet.pair}
                        >
                            <Text
                                variant="bodyMediumSize"
                                color="mid"
                            >
                                {t("blocks.invoice01.subtotal")}
                            </Text>
                            <Text
                                variant="bodyMediumSize"
                                color="high"
                            >
                                {currency.format(subtotal)}
                            </Text>
                        </View>
                        <View
                            style={stylesheet.pair}
                        >
                            <Text
                                variant="bodyMediumSize"
                                color="mid"
                            >
                                {t("blocks.invoice01.vat", [
                                    percent.format(VAT_RATE)
                                ])}
                            </Text>
                            <Text
                                variant="bodyMediumSize"
                                color="high"
                            >
                                {currency.format(vat)}
                            </Text>
                        </View>
                        <Card
                            variant="filled"
                        >
                            <Card.Content>
                                <View
                                    style={[
                                        stylesheet.pair,
                                        stylesheet.centerRow,
                                        {
                                            gap: spaces.spacingMd
                                        }
                                    ]}
                                >
                                    <Text
                                        variant="labelLargeSize"
                                        color="high"
                                    >
                                        {t("blocks.invoice01.total")}
                                    </Text>
                                    <Text
                                        variant="headlineSmallSize"
                                        color="high"
                                    >
                                        {currency.format(subtotal + vat)}
                                    </Text>
                                </View>
                            </Card.Content>
                        </Card>
                        <Text
                            variant="bodySmallSize"
                            style={{
                                textAlign: "right"
                            }}
                            color="mid"
                        >
                            {t("blocks.invoice01.totalWords")}
                        </Text>
                    </View>
                </View>
                <Separator
                    length="100%"
                />
                <View
                    style={{
                        gap: spaces.spacingSm
                    }}
                >
                    <Text
                        variant="labelMediumSize"
                        color="mid"
                    >
                        {t("blocks.invoice01.notes")}
                    </Text>
                    <Text
                        variant="bodyMediumSize"
                        color="mid"
                    >
                        {t("blocks.invoice01.notesBody")}
                    </Text>
                </View>
                <View
                    style={[
                        stylesheet.pair,
                        stylesheet.split,
                        {
                            gap: spaces.spacingSm
                        }
                    ]}
                >
                    <Text
                        variant="bodySmallSize"
                        color="mid"
                    >
                        {t("blocks.invoice01.thanks")}
                    </Text>
                    <Text
                        variant="bodySmallSize"
                        color="mid"
                    >
                        {t("blocks.invoice01.page")}
                    </Text>
                </View>
            </Card.Content>
        </Card>
    </ScrollView>;
};
export default Invoice01;
